import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import { useExpenseCategories, useExpenseSubCategories, ExpenseCategory, ExpenseSubCategory } from './useData';
import { moduleConfig, getColumns, getToolbarIcon, getTableActions } from './config';
import { useTheme } from '../../contexts/ThemeContext';
import ConfirmationDialog from '../../components/ui/ConfirmationDialog';
import { StandardTableActions } from '../../components/ui';
import { deleteExpenseSubCategory, deleteExpenseCategory } from '../../services/mutations';

const SPLITTER_STORAGE_KEY = 'expense-categories-manager-splitter';
const SPLITTER_MIN = 25;
const SPLITTER_MAX = 75;

const subCategoryColumns = [
  { key: 'name', header: 'Name', sortable: true, render: (value: string) => value || '-' },
];

const List: React.FC = () => {
  const navigate = useNavigate();
  const { colors } = useTheme();
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ id: number; name: string } | null>(null);
  const [deleteCategoryTarget, setDeleteCategoryTarget] = useState<{ id: number; name: string } | null>(null);
  const [splitterPosition, setSplitterPosition] = useState(() => {
    const stored = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(SPLITTER_STORAGE_KEY) : null;
    const n = stored != null ? Number(stored) : 50;
    return Number.isFinite(n) ? Math.max(SPLITTER_MIN, Math.min(SPLITTER_MAX, n)) : 50;
  });
  const [isResizing, setIsResizing] = useState(false);

  const { data: categories, isLoading: categoriesLoading, mutate: mutateCategories } = useExpenseCategories();
  const { data: allSubCategories, isLoading: subCategoriesLoading, mutate: mutateSubCategories } = useExpenseSubCategories();

  const subCategoriesForSelected = useMemo(() => {
    if (selectedCategoryId == null) return [];
    return allSubCategories.filter((s) => s.exp_category_id === selectedCategoryId);
  }, [allSubCategories, selectedCategoryId]);

  const selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId),
    [categories, selectedCategoryId]
  );

  const handleDeleteSubCategory = async (id: number) => {
    await deleteExpenseSubCategory(id);
    mutateSubCategories();
    setDeleteTarget(null);
  };

  const handleDeleteCategory = async (id: number) => {
    await deleteExpenseCategory(id);
    if (selectedCategoryId === id) setSelectedCategoryId(null);
    mutateCategories();
    mutateSubCategories();
    setDeleteCategoryTarget(null);
  };

  const handleSplitterMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  useEffect(() => {
    if (!isResizing) return;
    const onMove = (e: MouseEvent) => {
      const container = document.querySelector('[data-expense-categories-split-container]');
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const newPosition = ((e.clientX - rect.left) / rect.width) * 100;
      setSplitterPosition(Math.max(SPLITTER_MIN, Math.min(SPLITTER_MAX, newPosition)));
    };
    const onUp = () => setIsResizing(false);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing]);

  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(SPLITTER_STORAGE_KEY, String(splitterPosition));
  }, [splitterPosition]);

  const formatDate = () => '';
  const categoryColumns = getColumns(formatDate);

  return (
    <div className="flex flex-col h-full">
      <div className={`flex items-center justify-between gap-4 px-4 sm:px-6 py-3 mb-4 ${colors?.surface} border-b ${colors?.border}`}>
        <h1 className={`text-xl font-semibold ${colors?.text}`}>Expense Categories & Sub-categories</h1>
        <div className="flex items-center gap-2">
          <Link
            to={moduleConfig.module.route.new}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 text-sm font-medium"
          >
            {getToolbarIcon('add')}
            Add category
          </Link>
          {selectedCategoryId != null && (
            <Link
              to={`/p_crm_expense_subcategory/new?exp_category_id=${selectedCategoryId}`}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 text-sm font-medium"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add subcategory
            </Link>
          )}
        </div>
      </div>

      <div
        className="flex flex-1 min-h-0 relative"
        data-expense-categories-split-container
      >
        {/* Left: Categories */}
        <div
          className={`flex flex-col rounded-l-lg border ${colors?.border} ${colors?.surface} overflow-hidden min-h-[280px] shrink-0`}
          style={{ width: `${splitterPosition}%` }}
        >
          <div className={`px-4 py-3 border-b ${colors?.border} ${colors?.text} font-medium`}>
            Categories
          </div>
          <div className="flex-1 overflow-auto min-h-0">
            <DataTable<ExpenseCategory>
              data={categories}
              columns={categoryColumns}
              loading={categoriesLoading}
              keyExtractor={(row) => row.id}
              actions={(row) => getTableActions(row, (id) => setDeleteCategoryTarget({ id, name: row.name || `#${id}` }), undefined, undefined, { canDelete: true, canEdit: true })}
              actionsPosition="left"
              onRowClick={(row) => setSelectedCategoryId(row.id)}
              selectedRowId={selectedCategoryId}
              emptyMessage={moduleConfig.emptyStates.noData}
            />
          </div>
        </div>

        {/* Resizable splitter */}
        <div
          role="separator"
          aria-label="Resize panels"
          className={`absolute top-0 bottom-0 w-1 cursor-col-resize transition-colors z-10 shrink-0 opacity-50 hover:opacity-100 bg-gray-300 dark:bg-gray-600 ${isResizing ? '!opacity-100' : ''}`}
          style={{ left: `${splitterPosition}%`, transform: 'translateX(-50%)' }}
          onMouseDown={handleSplitterMouseDown}
        />

        {/* Right: Sub-categories */}
        <div
          className={`flex flex-col rounded-r-lg border ${colors?.border} ${colors?.surface} overflow-hidden min-h-[280px] flex-1 min-w-0`}
          style={{ width: `${100 - splitterPosition}%` }}
        >
          <div className={`px-4 py-3 border-b ${colors?.border} ${colors?.text} font-medium`}>
            Sub-categories
            {selectedCategory && (
              <span className="ml-2 text-sm font-normal opacity-80">
                for “{selectedCategory.name}”
              </span>
            )}
          </div>
          <div className="flex-1 overflow-auto min-h-0">
            {selectedCategoryId == null ? (
              <div className={`flex items-center justify-center h-full min-h-[200px] ${colors?.textSecondary} text-sm`}>
                Select a category to view its sub-categories.
              </div>
            ) : (
              <DataTable<ExpenseSubCategory>
                data={subCategoriesForSelected}
                columns={subCategoryColumns}
                loading={subCategoriesLoading}
                keyExtractor={(row) => row.id}
                actions={(row) => (
                  <StandardTableActions
                    viewTo={`/p_crm_expense_subcategory/${row.id}`}
                    editTo={`/p_crm_expense_subcategory/${row.id}/edit`}
                    onDelete={() => setDeleteTarget({ id: row.id, name: row.name || `#${row.id}` })}
                  />
                )}
                actionsPosition="left"
                onRowClick={(row) => navigate(`/p_crm_expense_subcategory/${row.id}`)}
                emptyMessage="No sub-categories for this category."
              />
            )}
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={deleteTarget != null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => { if (deleteTarget) handleDeleteSubCategory(deleteTarget.id); }}
        title="Delete sub-category"
        message={deleteTarget ? `Are you sure you want to delete "${deleteTarget.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ConfirmationDialog
        isOpen={deleteCategoryTarget != null}
        onClose={() => setDeleteCategoryTarget(null)}
        onConfirm={() => { if (deleteCategoryTarget) handleDeleteCategory(deleteCategoryTarget.id); }}
        title="Delete category"
        message={deleteCategoryTarget ? `Are you sure you want to delete "${deleteCategoryTarget.name}"? Sub-categories will be unlinked.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
      />
    </div>
  );
};

export default List;
